/*******************************************************************************
 * Copyright (c) 2025 Passport Fintech Inc. All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 ******************************************************************************/

import {
  ResetPasswordRequest,
  EmailResetPasswordRequest,
  emailResetPasswordRequestToApiResponse,
  EmailResetPasswordRequestToApiResponse,
  ResetPasswordRequestToApiResponse,
  resetPasswordRequestToApiRequest,
  ForgotLoginId,
  forgotLoginIdToApiRequest,
} from '@shared/modules/auth/models/change-password';
import {HttpClient, HttpHeaders, HttpResponse} from '@angular/common/http';
import {inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs/internal/Observable';
import {Credentials, credentialsToApiRequest} from '@shared/modules/auth/models/credentials';
import {EnvironmentConfig} from '@shared/models/environment-config';
import {APP_CONFIG} from '@common-config/app-config';
import {VerifyMfa} from '@shared/modules/auth/models/verify-mfa';
import {
  EmailVerification,
  emailVerificationToApiRequest,
  ResendEmailVerification,
  resendEmailVerificationToApiRequest,
} from '@shared/modules/auth/models/email-verification';
import {map} from 'rxjs';
import {
  PspToken,
  PspTokenApiResponse,
  pspTokenFromApiResponse,
} from '@shared/models/psp-token';
import {
  AdminLoginApiResponse,
  AdminLoginDetails,
  LoginApiResponse,
  LoginDetails,
  adminLoginDetailsFromResponse,
  loginDetailsFromResponse,
} from '@shared/modules/auth/models/login-details';
import {
  IamUser,
  IamUserApiResponse,
  iamUserFromApiResponse,
  IamUserInfos,
  IamUserInfosApiResponse,
  iamUserInfosFromApiResponse,
  IamUserResponse,
  IamUserResponseApiResponse,
  iamUserResponseFromApiResponse,
} from '@shared/modules/auth/models/iam-user';
import {Signup, signupToAPIPayload} from '@shared/modules/auth/models/signup';
import {
  IamAccount,
  IamAccountApiResponse,
  iamAccountFromApiResponse,
  ListIamAccountsNames,
  ListIamAccountsNamesApiResponse,
  listIamAccountsNamesFromApiResponse,
} from '@shared/modules/auth/models/iam-account';
import {
  NdaFileUrl,
  NdaFileUrlApiResponse,
  ndaFileUrlFromApiResponse,
} from '@shared/modules/auth/models/nda-file';
import {X_PSP_TYPE} from '@common-core/interceptors/auth/x-psp-type';
import {
  UserInvitationAcceptance,
  userInvitationAcceptanceToAPIPayload,
} from '@shared/modules/auth/models/user-invitation-acceptance';
import {
  TeamUser,
  TeamUserApiResponse,
  teamUserFromApiResponse,
} from '@shared/modules/settings/team/models/team';
import {
  ParticipantUser,
  ParticipantUserApiResponse,
  participantUserFromApiResponse,
} from '@shared/modules/auth/models/user-participant';
import {
  EntityToken,
  EntityTokenApiResponse,
  entityTokenFromApiResponse,
} from '@shared/modules/auth/models/entity-token';
import {
  IamToken,
  IamTokensApiResponse,
  iamTokenFromApiResponse,
} from '@shared/modules/platform/iam-tokens/models/iam-token';
import {HttpUtils} from '@shared/utils/http/http.utils';
import {VerifyType} from '@shared/modules/auth/models/verify-type';
import {
  SetupMfaRequest,
  SetupMfaApiResponse,
  setupMfaFromApiResponse,
  setupMfaToApiRequest,
  SetupMfaResponse,
} from '@shared/modules/auth/models/setup-mfa';

/**
 * This service is used for http requests.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private httpClient = inject(HttpClient);
  private appConfig = inject<EnvironmentConfig>(APP_CONFIG);

  APP_PREFIX = this.appConfig.cloudEnvironment.includes('gmf') ? '/gmf' : '';
  product =
    this.APP_PREFIX === '' && this.appConfig.cloudEnvironment.includes('paas')
      ? 'paas'
      : this.APP_PREFIX === '' && this.appConfig.cloudEnvironment.includes('spi')
        ? 'spi'
        : '';

  private withProduct(payload: any): any {
    return 'product' in payload || this.product === ''
      ? payload
      : {...payload, product: this.product};
  }

  private getBaseTokenOptions(useBaseToken: boolean): {headers?: HttpHeaders} {
    return useBaseToken
      ? {headers: new HttpHeaders({'X-PSP-Type': X_PSP_TYPE.useBaseToken})}
      : {};
  }

  /**
   * ============================================================================
   * SIGNUP SERVICES
   * ==========================================================================
   */

  // GMF

  /**
   * Service that execute the API request
   * @param user Signup model
   * @returns empty response
   */
  financialEntitySignUp(user: Signup): Observable<HttpResponse<void>> {
    return this.httpClient.post<HttpResponse<void>>(
      `${this.appConfig.apiBaseUrl}/gmf/iam/financial-entity/signup`,
      signupToAPIPayload(user)
    );
  }

  /**
   * Service that execute the API request
   * @param user Signup model
   * @returns empty response
   */
  dianSignUp(user: Signup): Observable<HttpResponse<void>> {
    return this.httpClient.post<HttpResponse<void>>(
      `${this.appConfig.apiBaseUrl}/iam/dian/signup`,
      signupToAPIPayload(user)
    );
  }

  // SPI

  /**
   * Service that execute the API request
   * @param user Signup model
   * @returns empty response
   */
  integratorSignUp(user: Signup): Observable<HttpResponse<void>> {
    return this.httpClient.post<HttpResponse<void>>(
      `${this.appConfig.apiBaseUrl}${this.APP_PREFIX}/iam/integrator/signup`,
      signupToAPIPayload(user)
    );
  }

  /**
   * Service that execute the API request
   * @param user Signup model
   * @returns empty response
   */
  participantSignUp(user: Signup): Observable<HttpResponse<void>> {
    return this.httpClient.post<HttpResponse<void>>(
      `${this.appConfig.apiBaseUrl}/iam/participant/signup`,
      signupToAPIPayload(user)
    );
  }

  /**
   * Service that execute the API request
   * @param user Signup model
   * @returns empty response
   */
  bankRepSignUp(user: Signup): Observable<HttpResponse<void>> {
    return this.httpClient.post<HttpResponse<void>>(
      `${this.appConfig.apiBaseUrl}/iam/banrep/signup`,
      signupToAPIPayload(user)
    );
  }

  // PaaS

  /**
   * Service that execute the API request
   * @param user Signup model
   * @returns empty response
   */
  entitySignUp(user: Signup): Observable<HttpResponse<void>> {
    return this.httpClient.post<HttpResponse<void>>(
      `${this.appConfig.apiBaseUrl}/iam/entity/signup`,
      signupToAPIPayload(user)
    );
  }

  /**
   * ============================================================================
   * LOGIN SERVICE
   * ==========================================================================
   */

  /**
   * Service that execute the API request
   * @param credentials Credentials model
   * @returns Observable with the http response
   */
  login(credentials: Credentials): Observable<LoginDetails> {
    const URL = credentials.loginId
      ? `${this.appConfig.apiBaseUrl}/gmf/iam/login`
      : `${this.appConfig.apiBaseUrlV2}/iam/login`;
    return this.httpClient
      .post<LoginApiResponse>(URL, credentialsToApiRequest(credentials))
      .pipe(map(response => loginDetailsFromResponse(response)));
  }

  /**
   * ============================================================================
   * EMAIL VERIFICATION SERVICES
   * ==========================================================================
   */

  // Signup

  /**
   * Service that execute the API request
   * @param data Email and OTP to verify
   * @returns Observable with the http response
   */
  emailVerification(data: EmailVerification): Observable<PspToken> {
    const URL = `${this.appConfig.apiBaseUrlV2}/iam/email/verify`;

    return this.httpClient
      .post<PspTokenApiResponse>(URL, emailVerificationToApiRequest(data))
      .pipe(map(response => pspTokenFromApiResponse(response)));
  }

  /**
   * Service that execute the API request
   * @param resendEmail - the resend email verification details
   * @returns Observable with the http response
   */
  resendEmailVerification(resendEmail: ResendEmailVerification): Observable<any> {
    const URL = `${this.appConfig.apiBaseUrlV2}/iam/email/resend-verification`;

    return this.httpClient.post<any>(URL, resendEmailVerificationToApiRequest(resendEmail));
  }

  /**
   * ============================================================================
   * PHONE VERIFICATION SERVICES
   * ==========================================================================
   */

  // Login

  // Signup

  /**
   * Service that execute the API request
   * @param phoneNumber Phone number to send the verification SMS
   * @param useBaseToken
   * @returns Observable with the http response
   */
  phoneVerification(phoneNumber: string, useBaseToken: boolean = false): Observable<any> {
    return this.httpClient.post<any>(
      `${this.appConfig.apiBaseUrlV2}/iam/mfa/sms`,
      {
        mobile_phone_number: phoneNumber,
      },
      this.getBaseTokenOptions(useBaseToken)
    );
  }

  /**
   * Service that execute the API request
   * @param useBaseToken
   * @returns Observable with the http response
   */
  resendSmsVerification(useBaseToken: boolean = false): Observable<any> {
    return this.httpClient.post<any>(
      `${this.appConfig.apiBaseUrlV2}/iam/mfa/resend-sms`,
      {},
      this.getBaseTokenOptions(useBaseToken)
    );
  }

  /**
   * Service that execute the API request
   * @param verifyData Data to verify the MFA
   * @param useBaseToken
   * @returns Observable with the http response
   */
  mfaPhoneVerification(
    verifyData: VerifyMfa,
    useBaseToken: boolean = false
  ): Observable<PspToken> {
    return this.httpClient
      .post<PspTokenApiResponse>(
        `${this.appConfig.apiBaseUrlV2}/iam/mfa/verify-setup`,
        verifyData,
        this.getBaseTokenOptions(useBaseToken)
      )
      .pipe(map(response => pspTokenFromApiResponse(response)));
  }

  /**
   * Service that execute the API request
   * @param otp OTP to verify
   * @param isRootUser Flag to indicate if the user is a passport user
   * @returns Observable with the http response
   */
  mfaLoginVerification(otp: string, isRootUser: boolean = false): Observable<PspToken> {
    const baseUrl = isRootUser ? this.appConfig.apiBaseUrl : this.appConfig.apiBaseUrlV2;

    return this.httpClient
      .post<PspTokenApiResponse>(`${baseUrl}/iam/mfa/verify`, {
        otp,
      })
      .pipe(map(response => pspTokenFromApiResponse(response)));
  }

  /**
   * ============================================================================
   * AUTHENTICATOR QR VERIFICATION SERVICES
   * ==========================================================================
   */

  /**
   * Service that execute the API request
   * @returns Observable with the type and provisioning URL
   */
  mfaLoginSetup(): Observable<string> {
    return this.setupMfa({type: VerifyType.QrCode}).pipe(
      map(response => response.provisioningUri!)
    );
  }

  /**
   * Service that execute the API request for MFA setup by type.
   * @param setupMfaRequest Type and optional phone number
   * @param useBaseToken
   * @returns Observable with the type and optional provisioning URL
   */
  setupMfa(
    setupMfaRequest: SetupMfaRequest,
    useBaseToken: boolean = false
  ): Observable<SetupMfaResponse> {
    return this.httpClient
      .post<SetupMfaApiResponse>(
        `${this.appConfig.apiBaseUrlV2}/iam/mfa/setup`,
        setupMfaToApiRequest(setupMfaRequest),
        this.getBaseTokenOptions(useBaseToken)
      )
      .pipe(map(response => setupMfaFromApiResponse(response)));
  }

  /**
   * Service that execute the API request
   * @param passwordReset Password reset data, email and accountId
   * @returns Observable with the http response
   */
  sendResetPasswordEmail(passwordReset: EmailResetPasswordRequest): Observable<any> {
    const URL = `${this.appConfig.apiBaseUrl}${this.APP_PREFIX}/iam/password/reset`;

    return this.httpClient.post<EmailResetPasswordRequestToApiResponse>(
      URL,
      emailResetPasswordRequestToApiResponse(this.withProduct(passwordReset))
    );
  }

  /**
   * Service that execute the API request
   * @param resetData Data to reset password
   * @returns Observable with the http response
   */
  resetPassword(resetData: ResetPasswordRequest): Observable<any> {
    const URL = `${this.appConfig.apiBaseUrl}${this.APP_PREFIX}/iam/password`;

    return this.httpClient.put<ResetPasswordRequestToApiResponse>(
      URL,
      resetPasswordRequestToApiRequest(this.withProduct(resetData))
    );
  }

  /**
   * Service that execute the API request
   * @param passwordData Data to set the password
   * @returns Observable with the http response
   */
  setPassword(passwordData: ResetPasswordRequest): Observable<PspToken> {
    return this.httpClient
      .post<PspTokenApiResponse>(
        `${this.appConfig.apiBaseUrl}/iam/signup-fe-from-integrator-invite`,
        resetPasswordRequestToApiRequest(passwordData)
      )
      .pipe(map(response => pspTokenFromApiResponse(response)));
  }

  /**
   * Service that execute the API request
   * @returns Observable with the http response
   */
  logout(): Observable<unknown> {
    return this.httpClient.post<unknown>(`${this.appConfig.apiBaseUrl}/iam/logout`, {});
  }

  /**
   * Get User details
   * @returns Observable<User> - user info
   */
  getUserProfiles(): Observable<IamUserInfos> {
    const headers = new HttpHeaders({
      'X-PSP-Type': X_PSP_TYPE.useBaseToken,
    });
    return this.httpClient
      .get<IamUserInfosApiResponse>(`${this.appConfig.apiBaseUrlV2}/iam/users`, {headers})
      .pipe(map(response => iamUserInfosFromApiResponse(response)));
  }

  /**
   * Get User details by calling V1 version of the API
   * @param userId The Id of the user
   * @returns Observable<IamUser> - user details
   */
  getUserProfile(userId: string): Observable<IamUser> {
    return this.httpClient
      .get<IamUserApiResponse>(`${this.appConfig.apiBaseUrl}/iam/users/${userId}`)
      .pipe(map(response => iamUserFromApiResponse(response)));
  }

  /**
   * Get User details by calling V2 version of the API
   * @param userId The Id of the user
   * @returns Observable<IamUserResponse> - user details
   */
  getUserProfilev2(userId: string): Observable<IamUserResponse> {
    const headers = new HttpHeaders({
      'X-PSP-Type': X_PSP_TYPE.useBaseToken,
    });
    return this.httpClient
      .get<IamUserResponseApiResponse>(`${this.appConfig.apiBaseUrlV2}/iam/users/${userId}`, {
        headers,
      })
      .pipe(map(response => iamUserResponseFromApiResponse(response)));
  }

  /**
   * Get Iam Account details
   * @param accountId The account Id of the user
   * @returns Observable<IamAccount> - IAM Account details
   */
  getIamAccount(accountId: string): Observable<IamAccount> {
    return this.httpClient
      .get<IamAccountApiResponse>(`${this.appConfig.apiBaseUrl}/iam/accounts/${accountId}`)
      .pipe(map(response => iamAccountFromApiResponse(response)));
  }

  /**
   * List IAM accounts names
   * @returns Observable<ListIamAccountsNames> - IAM Account list names
   */
  listAccountsNames(): Observable<ListIamAccountsNames> {
    return this.httpClient
      .get<ListIamAccountsNamesApiResponse>(`${this.appConfig.apiBaseUrl}/iam/account-names`)
      .pipe(map(response => listIamAccountsNamesFromApiResponse(response)));
  }

  /**
   * ============================================================================
   * CREATE USERS SERVICES
   * ==========================================================================
   */

  /**
   * Create Entity User in Core
   * @returns Observable<fintechUserId> - created fintech Id
   */
  createUserEntity(): Observable<EntityToken> {
    return this.httpClient
      .post<EntityTokenApiResponse>(`${this.appConfig.apiBaseUrl}/gmf/financial-entity`, {})
      .pipe(map(response => entityTokenFromApiResponse(response)));
  }

  /**
   * Create Integrator User in Core
   * @returns Observable<fintechUserId> - created fintech Id
   */
  createUserIntegrator(): Observable<EntityToken> {
    return this.httpClient
      .post<EntityTokenApiResponse>(`${this.appConfig.apiBaseUrl}/integrator`, {})
      .pipe(map(response => entityTokenFromApiResponse(response)));
  }

  /**
   * Create Participant User in Core
   * @returns Observable<fintechUserId> - created fintech Id
   */
  createUserParticipant(): Observable<EntityToken> {
    return this.httpClient
      .post<EntityTokenApiResponse>(`${this.appConfig.apiBaseUrl}/participant`, {})
      .pipe(map(response => entityTokenFromApiResponse(response)));
  }

  /**
   * Create Entities for PaaS
   * @param nit - Entity nit string
   * @returns Observable<fintechUserId> - created fintech Id
   */
  createEntities(nit: string): Observable<EntityToken> {
    return this.httpClient
      .post<EntityTokenApiResponse>(`${this.appConfig.apiBaseUrl}/paas/entities`, {nit})
      .pipe(map(response => entityTokenFromApiResponse(response)));
  }

  /**
   * Create Participant User in Core
   * @param root0 - Request data
   * @param root0.participantId - Participant Id
   * @returns Observable<fintechUserId> - created fintech Id
   */
  getUserParticipant({participantId}: {participantId: string}): Observable<ParticipantUser> {
    return this.httpClient
      .get<ParticipantUserApiResponse>(
        `${this.appConfig.apiBaseUrl}/participant/${participantId}`
      )
      .pipe(map(response => participantUserFromApiResponse(response)));
  }

  /**
   * Update Participant User in Core
   * @param root0 - Request data
   * @param root0.identificationNumber - Identification Number
   * @param root0.participantName - Participant Name
   * @returns Observable<fintechUserId> - created fintech Id
   */
  updateUserParticipant({
    identificationNumber,
    participantName,
  }: {
    identificationNumber: string;
    participantName: string;
  }): Observable<ParticipantUser> {
    return this.httpClient
      .patch<ParticipantUserApiResponse>(`${this.appConfig.apiBaseUrl}/participant`, {
        Identification: identificationNumber,
        Name: participantName,
      })
      .pipe(map(response => participantUserFromApiResponse(response)));
  }

  /**
   * Sign NDA in Core for new IAM accounts
   * @param root0 - Request data
   * @param root0.accountId - Account id
   * @param root0.signedNdaPdfBase64 - Signed NDA as PDF in base64 form
   * @returns Observable<unknown> - response
   */
  uploadNda({
    accountId,
    signedNdaPdfBase64,
  }: {
    accountId: string;
    signedNdaPdfBase64: string;
  }): Observable<unknown> {
    return this.httpClient.post<unknown>(
      `${this.appConfig.apiBaseUrl}/iam/accounts/${accountId}/nda`,
      {
        nda: signedNdaPdfBase64,
      }
    );
  }

  /**
   * Download NDA pdf presignedUrl
   * @param root0 - The request options
   * @param root0.accountId - The id of the IAM Account
   @returns Observable<NdaFileUrl> - presigned url for the signed nda
   */
  geNdaPresignedUrl({accountId}: {accountId: string}): Observable<NdaFileUrl> {
    return this.httpClient
      .post<NdaFileUrlApiResponse>(
        `${this.appConfig.apiBaseUrl}/iam/accounts/${accountId}/generate-nda-url`,
        {}
      )
      .pipe(map(response => ndaFileUrlFromApiResponse(response)));
  }

  /**
   * Google Single Sign On
   * @param requestOptions The request options
   * @param requestOptions.idToken - Object ID Token
   * @returns Observable<AdminLoginDetails>
   */
  googleSingleSignOn({idToken}: {idToken: string}): Observable<AdminLoginDetails> {
    return this.httpClient
      .post<AdminLoginApiResponse>(`${this.appConfig.apiBaseUrl}/admin/iam/sso`, {
        auth_code: idToken,
      })
      .pipe(map(response => adminLoginDetailsFromResponse(response)));
  }

  /**
   * Get client credentials token
   * @param requestParams - The request params
   * @param requestParams.clientId - The client id
   * @param requestParams.clientSecret - The client secret
   * @returns Observable<PspToken> - The client credentials token
   */
  getClientCredentialsToken({
    clientId,
    clientSecret,
  }: {
    clientId: string;
    clientSecret: string;
  }): Observable<PspToken> {
    return this.httpClient
      .post<PspTokenApiResponse>(`${this.appConfig.apiBaseUrl}/iam/oauth/tokens`, {
        grant_type: 'client_credentials',
        client_id: clientId,
        client_secret: clientSecret,
      })
      .pipe(map(response => pspTokenFromApiResponse(response)));
  }

  /**
   * Get customer token to assume a user identity
   * @param requestParams - The request params
   * @param requestParams.customerId - The customer id
   * @returns Observable<PspToken> - The customer token
   */
  getCustomerToken({customerId}: {customerId: string}): Observable<PspToken> {
    return this.httpClient
      .post<PspTokenApiResponse>(
        `${this.appConfig.apiBaseUrl}/iam/oauth/tokens/customers`,
        {
          customer_id: customerId,
        },
        {
          headers: {
            'X-PSP-Type': X_PSP_TYPE.getCustomerToken,
          },
        }
      )
      .pipe(map(response => pspTokenFromApiResponse(response)));
  }

  /**
   * Get client token to assume a user identity
   * @param requestParams - The request params
   * @param requestParams.financialEntityId - The financial entity id
   * @returns Observable<PspToken> - The client token
   */
  integratorManageFinancialEntity({
    financialEntityId,
  }: {
    financialEntityId: string;
  }): Observable<PspToken> {
    return this.httpClient
      .post<PspTokenApiResponse>(
        `${this.appConfig.apiBaseUrl}/integrator/manage/financial-entity`,
        {
          financial_entity_id: financialEntityId,
        },
        {
          headers: {
            'X-PSP-Type': X_PSP_TYPE.getFinancialEntityToken,
          },
        }
      )
      .pipe(map(response => pspTokenFromApiResponse(response)));
  }

  /**
   * Get client token to assume a user identity
   * @param requestParams - The request params
   * @param requestParams.participantId - The participant id
   * @returns Observable<PspToken> - The client token
   */
  spiIntegratorManageParticipant({
    participantId,
  }: {
    participantId: string;
  }): Observable<PspToken> {
    return this.httpClient
      .post<PspTokenApiResponse>(
        `${this.appConfig.apiBaseUrl}/integrator/manage/participant`,
        {
          participant_id: participantId,
        },
        {
          headers: {
            'X-PSP-Type': X_PSP_TYPE.getParticipantToken,
          },
        }
      )
      .pipe(map(response => pspTokenFromApiResponse(response)));
  }

  /**
   * Accept invitation to join an account
   * @param acceptance - The invitation acceptance
   * @returns Observable<TeamUser> - The team user
   */
  acceptInvitation(acceptance: UserInvitationAcceptance): Observable<TeamUser> {
    const URL = `${this.appConfig.apiBaseUrl}${this.APP_PREFIX}/iam/accounts/${acceptance.accountId}/user-invitations/accept`;

    return this.httpClient
      .post<TeamUserApiResponse>(URL, {
        ...userInvitationAcceptanceToAPIPayload(acceptance),
      })
      .pipe(map(response => teamUserFromApiResponse(response)));
  }

  // Client onboarding

  /**
   * Service that execute the API request
   * @param user Signup model
   * @returns Observable with the http response
   */
  clientSignUp(user: Signup): Observable<HttpResponse<void>> {
    return this.httpClient.post<HttpResponse<void>>(
      `${this.appConfig.apiBaseUrl}/gmf/iam/client/signup`,
      signupToAPIPayload(user)
    );
  }

  /**
   * Service that sends an email to retrieve login ID
   * @param forgotLoginId - the forgotten account id details
   * @returns empty response
   */
  forgotLoginId(forgotLoginId: ForgotLoginId): Observable<HttpResponse<void>> {
    return this.httpClient.post<HttpResponse<void>>(
      `${this.appConfig.apiBaseUrl}/gmf/iam/login-id/`,
      forgotLoginIdToApiRequest(forgotLoginId)
    );
  }

  /**
   * Get the last 10 tokens for a specific IAM account
   * @param root0 The request options
   * @param root0.accountId - the IAM account ID to filter by
   * @returns Observable<IamToken[]> - List of the last 10 tokens
   */
  getTokensForAccount({accountId}: {accountId: string}): Observable<IamToken[]> {
    // Generate query parameters to filter tokens by account ID and
    // pagination
    const params = HttpUtils.generateParamsFromObject({
      account_id: accountId,
      'page_params.page_size': 10,
      'page_params.page_number': 1,
      'order_params.order_key': 'created_at',
      'order_params.order_direction': 'DESC',
    });

    return this.httpClient
      .get<IamTokensApiResponse>(`${this.appConfig.apiBaseUrl}/iam/oauth/tokens`, {params})
      .pipe(map(response => response.tokens.map(iamTokenFromApiResponse)));
  }

  /**
   * Increase Team Size for an IAM Account
   * @param root0 The request options
   * @param root0.accountId - The account id
   * @param root0.teamSize - The new team size to set
   * @param root0.reason - The reason for increasing the team size
   * @returns Observable<HttpResponse<void>> - Empty response
   */
  increaseTeamSize({
    accountId,
    teamSize,
    reason,
  }: {
    accountId: string;
    teamSize: number;
    reason: string;
  }): Observable<HttpResponse<void>> {
    return this.httpClient.patch<HttpResponse<void>>(
      `${this.appConfig.apiBaseUrl}/iam/accounts/${accountId}/team-size`,
      {
        account_id: accountId,
        team_size: teamSize,
        reason: reason,
      }
    );
  }
}
